"use client";

import { forwardRef, useId, useImperativeHandle, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { addRecipientEmails, recipientSuggestions, type EmailRecipient } from "./email-recipients/recipients";

export type { EmailRecipient } from "./email-recipients/recipients";
export type EmailRecipientsInputHandle = { commit: () => EmailRecipient[] | null };

type Props = {
  value: EmailRecipient[];
  onChange: (recipients: EmailRecipient[]) => void;
  suggestions?: EmailRecipient[];
  suggestionsEnabled?: boolean;
  suggestionsLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  suggestionsError?: boolean;
  maxRecipients?: number;
  autoFocus?: boolean;
  onDraftChange?: (draft: string) => void;
};

const avatarColors = ["bg-rose-100 text-rose-700", "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-amber-100 text-amber-800", "bg-emerald-100 text-emerald-700"];
function avatarColor(email: string) {
  return avatarColors[Array.from(email).reduce((sum, character) => sum + character.charCodeAt(0), 0) % avatarColors.length];
}

export const EmailRecipientsInput = forwardRef<EmailRecipientsInputHandle, Props>(function EmailRecipientsInput({
  value, onChange, suggestions = [], suggestionsEnabled = true, suggestionsLabel, disabled = false, loading = false, suggestionsError = false,
  maxRecipients = 20, autoFocus = false, onDraftChange,
}, ref) {
  const t = useTranslations("emailRecipients");
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const options = suggestionsEnabled ? recipientSuggestions(value, suggestions, draft) : [];
  const showSuggestions = suggestionsEnabled && expanded && !disabled && value.length < maxRecipients && (options.length > 0 || loading || suggestionsError);
  const activeOption = options[activeIndex];

  function updateDraft(text: string) {
    setDraft(text);
    onDraftChange?.(text);
    setActiveIndex(-1);
  }

  function commit(text = draft) {
    if (disabled) return null;
    const result = addRecipientEmails(value, text, suggestions, maxRecipients);
    if (result.recipients.length !== value.length) onChange(result.recipients);
    updateDraft([...result.invalid, ...result.overflow].join(", "));
    const message = result.invalid.length > 0 ? t("invalid", { email: result.invalid[0] })
      : result.overflow.length > 0 ? t("limit", { count: maxRecipients }) : null;
    setError(message);
    return message ? null : result.recipients;
  }

  useImperativeHandle(ref, () => ({ commit }));

  function remove(email: string) {
    onChange(value.filter((person) => person.email !== email));
    setError(null);
    input.current?.focus();
  }

  return <div onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setExpanded(false);
      if (draft.trim()) commit();
    }
  }}>
    <div className="mb-2 flex items-center justify-between gap-3">
      <label className="text-sm font-medium text-text" htmlFor={id}>{t("label")}</label>
      <span className="text-xs tabular-nums text-text-muted" aria-label={t("count", { count: value.length, max: maxRecipients })}>{value.length} / {maxRecipients}</span>
    </div>
    <div className="relative">
      <div className={`flex min-h-20 flex-wrap content-start items-center gap-2 rounded-xl border bg-bg p-3 shadow-sm transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15 ${error ? "border-danger" : "border-border"} ${disabled ? "opacity-60" : ""}`} onClick={(event) => { if (event.target === event.currentTarget) input.current?.focus(); }}>
        {value.map((person) => <span key={person.email} className="inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-bg-elevated py-1 pl-1 pr-1.5 text-sm text-text" title={person.name ? `${person.name} <${person.email}>` : person.email}>
          <span aria-hidden="true" className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarColor(person.email)}`}>{(person.name ?? person.email).slice(0, 1).toUpperCase()}</span>
          <span className="min-w-0 truncate">{person.email}</span>
          <button type="button" disabled={disabled} onClick={() => remove(person.email)} aria-label={t("remove", { email: person.email })} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-lg leading-none text-text-muted transition hover:bg-surface-hover hover:text-text focus-visible:outline-2 focus-visible:outline-primary">×</button>
        </span>)}
        <input
          id={id} ref={input} role={suggestionsEnabled ? "combobox" : undefined}
          aria-autocomplete={suggestionsEnabled ? "list" : undefined}
          aria-expanded={suggestionsEnabled ? showSuggestions : undefined}
          aria-controls={showSuggestions ? `${id}-options` : undefined}
          aria-activedescendant={showSuggestions && activeOption ? `${id}-option-${activeIndex}` : undefined}
          aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : `${id}-hint`}
          className="min-w-36 flex-1 border-0 bg-transparent py-1.5 text-sm text-text outline-none placeholder:text-text-muted disabled:cursor-not-allowed"
          value={draft} disabled={disabled} autoFocus={autoFocus} autoComplete="off" autoCapitalize="none" spellCheck={false} inputMode="email"
          placeholder={value.length > 0 ? t("addMore") : t("placeholder")}
          onFocus={() => setExpanded(suggestionsEnabled)}
          onChange={(event) => { updateDraft(event.target.value); setExpanded(suggestionsEnabled); setError(null); }}
          onPaste={(event) => {
            const text = event.clipboardData.getData("text");
            if (/[\s,;<>]/.test(text)) {
              event.preventDefault();
              const field = event.currentTarget;
              commit(draft.slice(0, field.selectionStart ?? draft.length) + text + draft.slice(field.selectionEnd ?? draft.length));
            }
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === "Escape" && expanded) { event.preventDefault(); event.stopPropagation(); setExpanded(false); return; }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              if (options.length === 0) return;
              event.preventDefault(); setExpanded(true);
              setActiveIndex((current) => event.key === "ArrowDown" ? (current + 1) % options.length : current <= 0 ? options.length - 1 : current - 1);
              return;
            }
            if (["Enter", ",", ";"].includes(event.key)) {
              event.preventDefault();
              if (event.key === "Enter" && showSuggestions && activeOption) commit(activeOption.email);
              else if (draft.trim()) commit();
              return;
            }
            if (event.key === "Tab" && draft.trim()) commit(showSuggestions && activeOption ? activeOption.email : draft);
            if (event.key === "Backspace" && draft === "" && value.length > 0) { event.preventDefault(); remove(value[value.length - 1].email); }
          }}
        />
      </div>
      {showSuggestions ? <div className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-bg shadow-xl">
        <p className="px-3 pb-1 pt-3 text-xs font-medium text-text-muted">{suggestionsLabel ?? t("suggestions")}</p>
        <ul id={`${id}-options`} role="listbox" aria-label={suggestionsLabel ?? t("suggestions")} className="max-h-52 overflow-y-auto p-1.5">
          {options.map((person, index) => <li key={person.email} role="presentation">
            <button type="button" role="option" id={`${id}-option-${index}`} aria-selected={activeIndex === index} tabIndex={-1}
              className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition hover:bg-surface-hover ${activeIndex === index ? "bg-surface-hover" : ""}`}
              onMouseDown={(event) => event.preventDefault()} onClick={() => { commit(person.email); input.current?.focus(); }}>
              <span aria-hidden="true" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarColor(person.email)}`}>{(person.name ?? person.email).slice(0, 1).toUpperCase()}</span>
              <span className="min-w-0"><span className="block truncate text-sm font-medium text-text">{person.name ?? person.email}</span>{person.name ? <span className="block truncate text-xs text-text-muted">{person.email}</span> : null}</span>
              <span aria-hidden="true" className="ml-auto text-lg text-text-muted">+</span>
            </button>
          </li>)}
        </ul>
        {loading ? <p className="px-3 pb-3 text-xs text-text-muted">{t("loading")}</p> : suggestionsError ? <p className="px-3 pb-3 text-xs text-text-muted">{t("loadFailed")}</p> : null}
      </div> : null}
    </div>
    {error ? <p id={`${id}-error`} role="alert" className="mt-2 text-xs text-danger">{error}</p> : <p id={`${id}-hint`} className="mt-2 text-xs text-text-muted">{t("hint")}</p>}
  </div>;
});
